import { useEffect, useRef, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBreadcrumb } from "../../../context/BreadcrumbContext";
import TopicService from "../../../services/TopicService";
import AuthContext from "../../../auth/AuthContext";

const VideoStream = () => {
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { topicId, videoId } = useParams();
  const { setDynamicLabels } = useBreadcrumb();
  const authContext = useContext(AuthContext);

  const [topic, setTopic] = useState(null);
  const [allVideos, setAllVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentLessonId, setCurrentLessonId] = useState(videoId || null);
  const [loading, setLoading] = useState(true);

  const [completedVideoIds, setCompletedVideoIds] = useState(
    new Set()
  );

useEffect(() => {
  fetchVideoData();
}, [topicId]);

useEffect(() => {
  if (videoId) {
    setCurrentLessonId(videoId);
  }
}, [videoId]);


  const fetchVideoData = async () => {
    try {
      setLoading(true);

      const topicRes = await TopicService.GetTopicById(
        topicId,
        authContext
      );
      if (topicRes?.data) setTopic(topicRes.data);

      const subTopicsRes =
        await TopicService.GetAllSubTopicsByTopicId(
          topicId,
          authContext
        );

      if (!subTopicsRes?.data) return;

    const videos = [];

for (const subTopic of subTopicsRes.data.filter(st => !st.isDeleted)) {
  const videoRes =
    await TopicService.GetAllVideosBySubTopicId(
      subTopic.id,
      authContext
    );

  if (videoRes?.data) {
    videos.push(
      ...videoRes.data.map((v) => ({
        videoId: v.id,
        title: v.name,
        videoUrl: v.videoUrl,
        description: v.details || "No description available",
        hasQuiz: v.hasQuiz,
      }))
    );
  }
}


      setAllVideos(videos);
      setCurrentLessonId(videoId || videos[0]?.videoId);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allVideos.length && currentLessonId) {
      const found = allVideos.find(
        (v) => v.videoId === currentLessonId
      );
      setCurrentVideo(found || null);
    }
  }, [currentLessonId, allVideos]);

  /* BREADCRUMB */
  useEffect(() => {
    if (topic) {
      setDynamicLabels((p) => ({
        ...p,
        topicId,
        topicTitle: topic.name,
      }));
    }
    if (currentVideo) {
      setDynamicLabels((p) => ({
        ...p,
        videoTitle: currentVideo.title,
      }));
    }
  }, [topic, currentVideo, topicId]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-pulse">
        <div className="lg:col-span-3 bg-slate-300 h-[650px] rounded" />
        <div className="bg-slate-300 h-[650px] rounded" />
      </div>
    );
  }

  if (!currentVideo) {
    return (
      <div className="text-center mt-10 text-slate-500">
        Video not found
      </div>
    );
  }


  const playLesson = (lessonId) => {
    setCurrentLessonId(lessonId);

    setCompletedVideoIds((prev) => {
      const updated = new Set(prev);
      updated.add(lessonId);
      return updated;
    });

    navigate(
      `/knowledge-management/topics/${topicId}/video/${lessonId}`,
      { replace: true }
    );
  };

  /* PROGRESS */
  const completedCount = completedVideoIds.size;
  const progressPercent =
    allVideos.length > 0
      ? Math.round(
          (completedCount / allVideos.length) * 100
        )
      : 0;

  const goToQuiz = (lessonId) => {
    navigate(
      `/knowledge-management/topics/${topicId}/video/${lessonId}/quiz`
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* LEFT */}
      <div className="lg:col-span-3 flex flex-col gap-2">
      <div className="bg-black rounded overflow-hidden w-full aspect-video">
  <iframe
    key={currentLessonId}
    ref={videoRef}
    src={`${currentVideo.videoUrl}?autoplay=1`}
    className="w-full h-full"
    frameBorder="0"
    allow="autoplay; encrypted-media; picture-in-picture"
    allowFullScreen
  />
</div>

        <div>
          <h3 className="font-semibold mb-1">Details</h3>
          <p className="text-sm">{currentVideo.description}</p>

          {/* LEFT SIDE TAKE QUIZ */}
          {currentVideo.hasQuiz && (
            <button
              onClick={() => goToQuiz(currentLessonId)}
              className="mt-4 px-4 py-2 text-sm rounded bg-[#0e3a46] text-white"
            >
              Take Quiz →
            </button>
          )}
        </div>
      </div>

      {/* RIGHT */}
     <div
  className="
    bg-white dark:bg-slate-900
    border border-slate-200 dark:border-slate-700
    rounded p-4 max-h-[650px] flex flex-col
  "
>
  {/* Progress */}
  <div className="mb-4 text-sm text-slate-700 dark:text-slate-300">
    <div className="flex justify-between mb-1">
      <span>Progress</span>
      <span>
        {completedCount}/{allVideos.length} Completed
      </span>
    </div>

    <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded overflow-hidden">
      <div
        className="h-2 bg-green-600 rounded"
        style={{ width: `${progressPercent}%` }}
      />
    </div>
  </div>

  {/* Video List */}
  <div className="flex-1 overflow-y-auto space-y-3 pr-1">
 {allVideos.map((lesson, idx) => {
  const isActive = lesson.videoId === currentLessonId;
  const isCompleted = completedVideoIds.has(
    lesson.videoId
  );

  return (
    <div key={lesson.videoId}>
      <div
        onClick={() => playLesson(lesson.videoId)}
        className={`
          flex items-center justify-between p-2 rounded cursor-pointer
          transition 
          ${
            isActive
              ? "bg-blue-100 dark:bg-slate-700"
              : "hover:bg-slate-100 dark:hover:bg-slate-800"
          }
        `}
      >
        <div className="flex items-center gap-3">
          <span
            className={`
              w-6 h-6 rounded-full flex items-center justify-center text-xs
              ${
                isCompleted
                  ? "bg-green-600 text-white"
                  : "bg-slate-300 dark:bg-slate-600 text-slate-800 dark:text-white"
              }
            `}
          >
            {idx + 1}
          </span>

          <span
            className={`
              text-sm font-medium
              ${
                isActive
                  ? "text-blue-700 dark:text-blue-400"
                  : "text-slate-800 dark:text-slate-200"
              }
            `}
          >
            {lesson.title}
          </span>
        </div>

        <span
          className={`
            text-xs px-2 py-1 rounded-full text-white
            ${
              isActive
                ? "bg-blue-600"
                : isCompleted
                ? "bg-green-600"
                : "bg-black dark:bg-slate-700"
            }
          `}
        >
          ▶
        </span>
      </div>

      {/* Take Quiz */}
      {lesson.hasQuiz && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            goToQuiz(lesson.videoId);
          }}
          className="ml-11 mt-1 flex items-center gap-2 text-xs cursor-pointer"
        >
          <span
            className={`
              w-5 h-5 rounded-full
              ${
                isCompleted
                  ? "bg-green-600"
                  : "bg-slate-300 dark:bg-slate-600"
              }
            `}
          />
          <span className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
            Take Quiz →
          </span>
        </div>
      )}
    </div>
  );
})}

  </div>
</div>

    </div>
  );
};

export default VideoStream;
